// ============================================================================
// DashboardCards — Personal Finance V2 (Blueprint §5.2)
// Grid de KPI cards — PADRÃO OURO: layout 100% consistente.
// Linha 1: label + percentual
// Linha 2: valor principal (grande)
// Linha 3 (opcional): barra de progresso
// Linha 4 (opcional): "restam / meta" OU chips de status
// ============================================================================

import React from "react";

const formatCurrency = (val) =>
  new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(val);

const formatCompact = (val) =>
  new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
    notation: "compact",
  }).format(val);

export function DashboardCards({ dashboardCards, summary }) {
  const realExpense = summary.realExpense ?? 0;
  const totalPendingExpenses = summary.realPending ?? 0;
  const totalForecastExpenses = 0;
  const savingsRate = summary.savingsRate ?? 0;
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
      {dashboardCards.map(([key, cat]) => {
        const isIncome = key === "RECEITAS";
        const isSynthetic = key.startsWith("__");
        const isBalance = key === "__BALANCE__";
        const isExpenses = key === "__EXPENSES__";
        const isPending = key === "__PENDING__";

        const groupData = isExpenses
          ? {
              total: realExpense,
              paid: realExpense - totalPendingExpenses,
              pending: totalPendingExpenses,
              forecast: totalForecastExpenses,
            }
          : isBalance
            ? { total: summary.balance || 0, paid: 0, pending: 0, forecast: 0 }
            : isPending
              ? {
                  total: totalPendingExpenses,
                  paid: 0,
                  pending: totalPendingExpenses,
                  forecast: 0,
                }
              : summary.byGroup?.[key] || {
                  total: 0,
                  paid: 0,
                  pending: 0,
                  forecast: 0,
                };

        const val = groupData.total;
        const paidVal = groupData.paid;
        const pendingVal = groupData.pending;
        const forecastVal = groupData.forecast || 0;
        const budget = cat.budget || 0;
        const percent = budget > 0 ? (val / budget) * 100 : 0;
        const isOver = percent > 100;
        const remaining = Math.max(budget - val, 0);
        // Só trata como "emoji" um símbolo pictográfico de verdade — sem
        // isso, um grupo criado sem emoji no nome (ex: "ESSENCIAIS", criado
        // via combobox) tinha a palavra inteira capturada como "emoji" e
        // duplicada na tela ("ESSENCIAIS ESSENCIAIS"). Achado 17/09/2026.
        const rawLabel = cat.label || key;
        const emojiMatch = rawLabel.match(/^\p{Extended_Pictographic}+\s*/u);
        const emoji = emojiMatch ? emojiMatch[0].trim() : "";
        const cleanLabel = (
          emojiMatch ? rawLabel.slice(emojiMatch[0].length) : rawLabel
        ).trim();

        const hasPaid = paidVal > 0;
        const hasPending = pendingVal > 0;
        const hasForecast = forecastVal > 0;
        const hasBudget = !isIncome && budget > 0;

        return (
          <div
            key={key}
            className={`bg-white rounded-md border px-3 py-2.5 flex flex-col gap-2 hover:shadow-sm transition-shadow ${
              isIncome
                ? "border-emerald-500 ring-1 ring-emerald-100 shadow-sm"
                : isBalance
                  ? val < 0
                    ? "border-red-300"
                    : "border-emerald-300"
                  : isSynthetic
                    ? "border-slate-300"
                    : isOver
                      ? "border-red-300"
                      : "border-slate-200"
            }`}
          >
            {/* Linha 1: label + badge (budget% ou savings rate) */}
            <div className="flex items-center justify-between gap-2">
              <span
                className="text-[10.5px] font-semibold text-slate-600 uppercase tracking-wide truncate"
                title={cleanLabel}
              >
                <span className="mr-1 opacity-70">{emoji}</span>
                {cleanLabel}
              </span>
              {isBalance && (
                <span
                  className={`text-[10.5px] font-bold tabular-nums flex-shrink-0 ${
                    savingsRate > 0
                      ? "text-emerald-600"
                      : savingsRate < 0
                        ? "text-red-600"
                        : "text-slate-400"
                  }`}
                >
                  {savingsRate > 0 ? "+" : ""}
                  {savingsRate}%
                </span>
              )}
              {hasBudget && (
                <span
                  className={`text-[10.5px] font-bold tabular-nums flex-shrink-0 ${
                    isOver
                      ? "text-red-600"
                      : percent >= 80
                        ? "text-amber-600"
                        : "text-slate-500"
                  }`}
                >
                  {Math.round(percent)}%
                </span>
              )}
            </div>

            {/* Linha 2: valor principal — SEMPRE SOZINHO, sem chips nem nada */}
            <div className="flex items-baseline justify-between gap-1">
              <span
                className={`font-mono font-bold tabular-nums text-[15px] leading-none ${
                  val === 0
                    ? "text-slate-300"
                    : isIncome
                      ? "text-emerald-700"
                      : isBalance
                        ? val < 0
                          ? "text-red-700"
                          : "text-emerald-700"
                        : isExpenses
                          ? "text-red-700"
                          : isPending
                            ? "text-amber-700"
                            : "text-slate-800"
                }`}
              >
                {formatCurrency(val)}
              </span>
            </div>

            {/* Linha 3 (se tiver budget): barra de progresso */}
            {hasBudget && (
              <div className="relative h-2 w-full bg-slate-200 rounded-sm overflow-hidden">
                {paidVal > 0 && (
                  <div
                    className={`absolute top-0 left-0 h-full ${isOver ? "bg-red-600" : "bg-blue-700"}`}
                    style={{
                      width: `${Math.min((paidVal / budget) * 100, 100)}%`,
                    }}
                  />
                )}
                {pendingVal > 0 && (
                  <div
                    className={`absolute top-0 h-full ${isOver ? "bg-red-300" : "bg-blue-300"}`}
                    style={{
                      left: `${Math.min((paidVal / budget) * 100, 100)}%`,
                      width: `${Math.min((pendingVal / budget) * 100, 100 - Math.min((paidVal / budget) * 100, 100))}%`,
                    }}
                  />
                )}
                {forecastVal > 0 && (
                  <div
                    className="absolute top-0 h-full bg-purple-300"
                    style={{
                      left: `${Math.min(((paidVal + pendingVal) / budget) * 100, 100)}%`,
                      width: `${Math.min((forecastVal / budget) * 100, 100 - Math.min(((paidVal + pendingVal) / budget) * 100, 100))}%`,
                    }}
                  />
                )}
              </div>
            )}

            {/* Linha 4 (sempre a mesma): restam/meta OU chips de status OU nada */}
            <div>
              {hasBudget ? (
                /* Com orçamento: restam / meta */
                <div className="flex flex-col text-[9.5px] text-slate-500 leading-tight text-right">
                  <span>meta {formatCurrency(budget)}</span>
                  {isOver ? (
                    <span className="text-red-600">
                      excedeu {formatCompact(val - budget)}
                    </span>
                  ) : (
                    <span>
                      restam{" "}
                      <span className="text-slate-500">
                        {formatCurrency(remaining)}
                      </span>
                    </span>
                  )}
                </div>
              ) : !isIncome &&
                !isSynthetic &&
                (hasPaid || hasPending || hasForecast) ? (
                /* Sem orçamento mas com status: chips (apenas cards de categoria, não sintéticos) */
                <div className="flex flex-wrap gap-1">
                  {hasPaid && (
                    <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[8.5px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                      Pago {formatCompact(paidVal)}
                    </span>
                  )}
                  {hasPending && (
                    <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[8.5px] font-bold bg-amber-50 text-amber-700 border border-amber-200">
                      <span className="w-1.5 h-1.5 rounded-full bg-amber-400" />
                      Pend. {formatCompact(pendingVal)}
                    </span>
                  )}
                  {hasForecast && (
                    <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[8.5px] font-bold bg-purple-50 text-purple-600 border border-purple-200">
                      <span className="w-1.5 h-1.5 rounded-full bg-purple-400" />
                      Prev. {formatCompact(forecastVal)}
                    </span>
                  )}
                </div>
              ) : null}
            </div>
          </div>
        );
      })}
    </div>
  );
}
